import React from 'react';
import { Vec3 } from 'playcanvas';

import CollapsibleSection from './collapsible-section';
import { t } from '../../translation';
import { ObserverData, SetProperty } from '../../types';

interface PositionsMenuProps {
    isOpen: boolean;
    onToggle: () => void;
    observerData: ObserverData;
    setProperty: SetProperty;
}

/**
 * Reusable setCameraView logic extracted from PositionPanel.
 * Moves the orbit camera to a predefined position around the scene origin
 * without rotating the PLY entity itself.
 */
const setCameraView = (viewType: string): void => {
    if (!window.viewer) return;

    const viewer = window.viewer;
    const multiCamera = viewer.multiCamera;
    const sceneBounds = viewer.sceneBounds || viewer.dynamicSceneBounds;

    if (!sceneBounds || !sceneBounds.center) return;

    const distance = 8;
    multiCamera.sceneSize = distance * 0.5;

    const d45 = distance / Math.sqrt(2);
    const focusPosition = new Vec3(0, 0, 0);
    let targetPosition: Vec3;

    switch (viewType) {
        case 'front':      targetPosition = new Vec3(0,    0.3,  distance); break;
        case 'frontRight': targetPosition = new Vec3(d45,  0.3,  d45);      break;
        case 'right':      targetPosition = new Vec3(distance, 0, 0);       break;
        case 'rightBack':  targetPosition = new Vec3(d45,  0.3, -d45);      break;
        case 'behind':     targetPosition = new Vec3(0,    0.3, -distance); break;
        case 'backLeft':   targetPosition = new Vec3(-d45, 0.3, -d45);      break;
        case 'left':       targetPosition = new Vec3(-distance, 0, 0);      break;
        case 'leftFront':  targetPosition = new Vec3(-d45, 0.3,  d45);      break;
        case 'top':        targetPosition = new Vec3(0,    distance, 0);    break;
        default: return;
    }

    // Compute orbit angles directly to avoid focus() issues with negative-Z views.
    const dir = new Vec3();
    dir.sub2(targetPosition, focusPosition);
    const hDist = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
    const elevDeg = Math.atan2(dir.y, hDist) * (180 / Math.PI);
    const azimDeg = Math.atan2(dir.x, dir.z) * (180 / Math.PI);

    const mc = multiCamera as any;
    mc._origin.copy(focusPosition);
    mc._position.copy(focusPosition);
    mc._dir.set(-elevDeg, -azimDeg);
    mc._angles.set(-elevDeg, -azimDeg, 0);
    mc._zoomDist = distance;

    viewer.renderNextFrame();
};

const PositionsMenu: React.FC<PositionsMenuProps> = ({ isOpen, onToggle }) => {
    return (
        <div className="positions-menu">
            <button
                className="positions-menu__toggle"
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span className="positions-menu__toggle-label">{t('ui.positions')}</span>
                <span className={`positions-menu__toggle-arrow ${isOpen ? 'positions-menu__toggle-arrow--open' : ''}`}>▶</span>
            </button>

            <CollapsibleSection isOpen={isOpen}>
                <div className="positions-menu__grid">
                    <button className="positions-menu__btn" onClick={() => setCameraView('leftFront')}>
                        {t('position.leftFront')}
                    </button>
                    <button className="positions-menu__btn" onClick={() => setCameraView('front')}>
                        {t('position.front')}
                    </button>
                    <button className="positions-menu__btn" onClick={() => setCameraView('frontRight')}>
                        {t('position.frontRight')}
                    </button>

                    <button className="positions-menu__btn" onClick={() => setCameraView('left')}>
                        {t('position.left')}
                    </button>
                    {/* Center cell empty */}
                    <div className="positions-menu__empty" />
                    <button className="positions-menu__btn" onClick={() => setCameraView('right')}>
                        {t('position.right')}
                    </button>

                    <button className="positions-menu__btn" onClick={() => setCameraView('backLeft')}>
                        {t('position.backLeft')}
                    </button>
                    <button className="positions-menu__btn" onClick={() => setCameraView('behind')}>
                        {t('position.back')}
                    </button>
                    <button className="positions-menu__btn" onClick={() => setCameraView('rightBack')}>
                        {t('position.rightBack')}
                    </button>
                </div>
            </CollapsibleSection>
        </div>
    );
};

export default PositionsMenu;
